/**
 * Base do estado pessoal por sinal.
 *
 * Favoritos, histórico, progresso e status offline compartilham a mesma linha em
 * `localSignState`. Concentrar a escrita aqui garante que nenhum desses recursos
 * apague acidentalmente os campos dos outros — o erro clássico seria um `put()`
 * de objeto parcial zerando o progresso ao favoritar.
 */
import { db } from '@/db/schema';
import { emptyLocalSignState, type LocalSignState } from '@/types/catalog';

export async function getState(signId: string): Promise<LocalSignState> {
  return (await db.localSignState.get(signId)) ?? emptyLocalSignState(signId);
}

export async function getStates(signIds: string[]): Promise<Map<string, LocalSignState>> {
  const rows = await db.localSignState.bulkGet(signIds);
  const map = new Map<string, LocalSignState>();
  signIds.forEach((signId, index) => {
    map.set(signId, rows[index] ?? emptyLocalSignState(signId));
  });
  return map;
}

/**
 * Aplica uma alteração parcial preservando todo o resto do registro.
 *
 * Sempre leia-modifique-escreva dentro da mesma transação: duas telas podem tocar
 * o mesmo sinal (favoritar enquanto um download progride).
 */
export async function updateState(
  signId: string,
  change: (current: LocalSignState) => LocalSignState,
): Promise<LocalSignState> {
  return db.transaction('rw', db.localSignState, async () => {
    const current = (await db.localSignState.get(signId)) ?? emptyLocalSignState(signId);
    const next = change(current);
    await db.localSignState.put(next);
    return next;
  });
}

export async function countStates(): Promise<number> {
  return db.localSignState.count();
}

/**
 * Remove estados pessoais órfãos — de sinais que não existem mais no catálogo.
 *
 * Nunca chamado automaticamente pela sincronização (PLAN §41): um sinal removido da
 * fonte pode voltar, e o favorito do usuário não deve evaporar junto. Fica disponível
 * como ferramenta explícita de limpeza.
 */
export async function pruneOrphanStates(): Promise<string[]> {
  return db.transaction('rw', db.signs, db.localSignState, async () => {
    const signIds = new Set(await db.signs.toCollection().primaryKeys());
    const orphans = (await db.localSignState.toCollection().primaryKeys()).filter(
      (signId) => !signIds.has(signId),
    );
    await db.localSignState.bulkDelete(orphans);
    return orphans;
  });
}
